import { CommonModule as NgCommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';

import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

import { MatNativeDateModule } from '@angular/material/core';
import { FormatDatePipe } from '@sondermind/data-access/pipes/format-date.pipe';
import { LocationNamePipe } from '@sondermind/data-access/pipes/location-name.pipe';
import { NoShowTypeNamePipe } from '@sondermind/data-access/pipes/no-show-type-name.pipe';
import { SystemTimezonePipe } from '@sondermind/data-access/pipes/system-timezone.pipe';
import { UserTimezonePipe } from '@sondermind/data-access/pipes/user-timezone.pipe';
import {
  EmbeddedClientAccountPendingComponent,
  EmbeddedClientAccountRequiredComponent,
  EmbeddedClientCreatorComponent,
  EmbeddedClientCreatorDialogComponent
} from '@sondermind/features/embedded-client';
import { ErrorListComponent } from '@sondermind/features/error-list';
import { AlertModalComponent } from '@sondermind/features/modals/alert';
import { ConfirmationModalComponent } from '@sondermind/features/modals/confirmation';
import {
  UnsignedAgreementModalComponent
} from '@sondermind/features/modals/unsigned-agreement';
import {
  NewSetPasswordComponent,
  SetPasswordComponent
} from '@sondermind/features/set-password';
import { IrisButtonModule } from '@sondermind/iris/button';
import { IrisCheckboxModule } from '@sondermind/iris/checkbox';
import { IrisFormFieldModule } from '@sondermind/iris/form-field';
import { IrisIconComponent } from '@sondermind/iris/icon';
import { IrisTextInputModule } from '@sondermind/iris/text-input';

import { GoogleMapsAutoCompleteDirective } from '@sondermind/google-maps';
import { DirectiveModule } from '@sondermind/utilities/directives';
import { Environment, ENVIRONMENT } from '@sondermind/utilities/models-environment';
import { FormatTimeAgoPipe, JoinArrayPipe } from '@sondermind/utilities/pipes';
import { CapitalizePipe } from '@sondermind/utilities/pipes/capitalize.pipe';
import { FormatPhonePipe } from '@sondermind/utilities/pipes/format-phone.pipe';
import { FormatServerDatePipe } from '@sondermind/utilities/pipes/format-server-date.pipe';
import { MinutesAfterHoursPipe } from '@sondermind/utilities/pipes/minutes-after-hours.pipe';
import { MinutesToHoursPipe } from '@sondermind/utilities/pipes/minutes-to-hours.pipe';
import { SafePipe } from '@sondermind/utilities/pipes/safe.pipe';
import { SuperBillStatePipe } from '@sondermind/utilities/pipes/superbill-state.pipe';

import { IrisCardModule } from '@sondermind/iris/card';
import { IrisDropdownModule } from '@sondermind/iris/dropdown';
import { AccountInfoCardComponent } from './components/account-info-card/account-info-card.component';
import { FormFieldErrorBelowComponent } from './components/form-field-error-below/form-field-error-below.component';
import { SaveButtonMaterialComponent } from './components/save-button-material/save-button-material.component';
import { CSSPropsDirective } from './directives/css-props.directive';
import { PluralizePipe } from './pipes/pluralize.pipe';

const COMPONENTS = [
  AlertModalComponent,
  AccountInfoCardComponent,
  ConfirmationModalComponent,
  EmbeddedClientAccountRequiredComponent,
  EmbeddedClientAccountPendingComponent,
  EmbeddedClientCreatorComponent,
  EmbeddedClientCreatorDialogComponent,
  FormFieldErrorBelowComponent,
  SaveButtonMaterialComponent,
  SetPasswordComponent,
  NewSetPasswordComponent,
  UnsignedAgreementModalComponent,
];

const DIRECTIVES = [
  CSSPropsDirective
];

const PIPES = [
  CapitalizePipe,
  FormatServerDatePipe,
  FormatPhonePipe,
  LocationNamePipe,
  MinutesToHoursPipe,
  MinutesAfterHoursPipe,
  SafePipe,
  SuperBillStatePipe,
  UserTimezonePipe,
  SystemTimezonePipe,
  PluralizePipe,
  NoShowTypeNamePipe,
];

@NgModule({
  declarations: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,
  ],
  imports: [
    NgCommonModule,
    FormsModule,
    FlexLayoutModule,
    ReactiveFormsModule,
    NgxMaskDirective,
    MatDatepickerModule,
    MatGridListModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatSelectModule,
    MatNativeDateModule,
    IrisButtonModule,
    IrisCheckboxModule,
    IrisFormFieldModule,
    IrisIconComponent,
    MatDividerModule,
    IrisTextInputModule,
    DirectiveModule,
    FormatTimeAgoPipe,
    GoogleMapsAutoCompleteDirective,
    IrisDropdownModule,
    IrisCardModule,
    IrisCardModule,
    GoogleMapsAutoCompleteDirective,
    JoinArrayPipe, // used in form-field-error-below

    // Standalone
    ErrorListComponent,
    FormatDatePipe
  ],
  exports: [
    ...COMPONENTS,
    ...DIRECTIVES,
    ...PIPES,
  ],
  providers: [
    provideNgxMask()
  ]
})
export class SondermindCommonModule {
  static forRoot(environment: Environment): ModuleWithProviders<SondermindCommonModule> {
    return {
      ngModule: SondermindCommonModule,
      providers: [
        { provide: ENVIRONMENT, useValue: environment },
        { provide: MAT_DIALOG_DATA, useValue: {} }
      ]
    };
  }
}
